import { z } from "zod/v4";

import { z_JsonValue } from "./chat-protocol";

export const z_ChatV8ElicitationMode = z.enum([
  "single_select",
  "multi_select",
  "free_text",
  "form",
]);
export type T_ChatV8ElicitationMode = z.infer<typeof z_ChatV8ElicitationMode>;

export const z_ChatV8ElicitationFieldKind = z.enum([
  "text",
  "number",
  "date",
  "single_select",
  "multi_select",
]);
export type T_ChatV8ElicitationFieldKind = z.infer<
  typeof z_ChatV8ElicitationFieldKind
>;

export const CHAT_V8_COMPANY_ANALYSIS_FLOW_NAME = "company_analysis";

export const z_ChatV8ElicitationOption = z.object({
  optionCuid: z.string(),
  label: z.string(),
  description: z.string().nullish(),
  value: z_JsonValue,
});
export type T_ChatV8ElicitationOption = z.infer<
  typeof z_ChatV8ElicitationOption
>;

export const z_ChatV8ElicitationField = z.object({
  fieldCuid: z.string(),
  label: z.string(),
  kind: z_ChatV8ElicitationFieldKind,
  required: z.boolean(),
  options: z.array(z_ChatV8ElicitationOption).nullish(),
});
export type T_ChatV8ElicitationField = z.infer<typeof z_ChatV8ElicitationField>;

export const REQUEST_CLARIFICATION_TOOL_NAME = "requestClarification";

const z_ChatV8RequestClarificationOptionInput = z.object({
  label: z.string(),
  description: z.string().optional(),
  value: z_JsonValue.optional(),
});
export type T_ChatV8RequestClarificationOptionInput = z.infer<
  typeof z_ChatV8RequestClarificationOptionInput
>;

const z_ChatV8RequestClarificationFieldInput = z.object({
  label: z.string(),
  kind: z_ChatV8ElicitationFieldKind,
  required: z.boolean().default(false),
  options: z.array(z_ChatV8RequestClarificationOptionInput).optional(),
});
export type T_ChatV8RequestClarificationFieldInput = z.infer<
  typeof z_ChatV8RequestClarificationFieldInput
>;

export const z_ChatV8RequestClarificationToolInput = z.object({
  title: z.string(),
  prompt: z.string(),
  mode: z_ChatV8ElicitationMode,
  required: z.boolean().default(true),
  allowOther: z.boolean().optional(),
  options: z.array(z_ChatV8RequestClarificationOptionInput).optional(),
  fields: z.array(z_ChatV8RequestClarificationFieldInput).optional(),
});
export type T_ChatV8RequestClarificationToolInput = z.infer<
  typeof z_ChatV8RequestClarificationToolInput
>;

export const chatV8ElicitationRequestShape = {
  title: z.string(),
  prompt: z.string(),
  mode: z_ChatV8ElicitationMode,
  required: z.boolean(),
  allowOther: z.boolean().optional(),
  options: z.array(z_ChatV8ElicitationOption).optional(),
  fields: z.array(z_ChatV8ElicitationField).optional(),
};

export const z_ChatV8ElicitationRequestPayload = z.object(
  chatV8ElicitationRequestShape,
);
export type T_ChatV8ElicitationRequestPayload = z.infer<
  typeof z_ChatV8ElicitationRequestPayload
>;

function normalizeElicitationOption(args: {
  createCuid: () => string;
  option: T_ChatV8RequestClarificationOptionInput;
}): T_ChatV8ElicitationOption {
  return {
    optionCuid: args.createCuid(),
    label: args.option.label,
    description: args.option.description ?? null,
    value: args.option.value ?? args.option.label,
  };
}

export function normalizeChatV8ElicitationToolInput(args: {
  createCuid: () => string;
  input: T_ChatV8RequestClarificationToolInput;
}): T_ChatV8ElicitationRequestPayload {
  const fields = args.input.fields?.map((field) => ({
    fieldCuid: args.createCuid(),
    label: field.label,
    kind: field.kind,
    required: field.required,
    ...(field.options
      ? {
          options: field.options.map((option) =>
            normalizeElicitationOption({
              createCuid: args.createCuid,
              option,
            }),
          ),
        }
      : {}),
  }));

  return z_ChatV8ElicitationRequestPayload.parse({
    title: args.input.title,
    prompt: args.input.prompt,
    mode: args.input.mode,
    required: args.input.required,
    ...(args.input.allowOther === undefined
      ? {}
      : { allowOther: args.input.allowOther }),
    ...(args.input.options
      ? {
          options: args.input.options.map((option) =>
            normalizeElicitationOption({
              createCuid: args.createCuid,
              option,
            }),
          ),
        }
      : {}),
    ...(fields ? { fields } : {}),
  });
}

export const z_ChatV8ElicitationAnswer = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("single_select"),
    selectedOptionCuid: z.string(),
    otherText: z.string().optional(),
  }),
  z.object({
    kind: z.literal("multi_select"),
    selectedOptionCuids: z.array(z.string()),
    otherText: z.string().optional(),
  }),
  z.object({
    kind: z.literal("free_text"),
    text: z.string(),
  }),
  z.object({
    kind: z.literal("form"),
    fields: z.record(z.string(), z_JsonValue),
  }),
]);
export type T_ChatV8ElicitationAnswer = z.infer<
  typeof z_ChatV8ElicitationAnswer
>;

export const CHAT_V8_PORTFOLIO_RATING_ELICITATION: T_ChatV8ElicitationRequestPayload =
  {
    title: "Rate my portfolio",
    prompt:
      "Before I rate it — three quick questions so the score fits your situation.",
    mode: "form",
    required: true,
    fields: [
      {
        fieldCuid: "rating-q1",
        label: "Is this most of your investing money, or one slice?",
        kind: "single_select",
        required: true,
        options: [
          {
            optionCuid: "rating-q1-most",
            label: "Most of it",
            value: "Most of it",
          },
          {
            optionCuid: "rating-q1-slice",
            label: "One slice",
            value: "One slice",
          },
          {
            optionCuid: "rating-q1-risky",
            label: "Just my risky account",
            value: "Just my risky account",
          },
        ],
      },
      {
        fieldCuid: "rating-q2",
        label: "When do you need most of this money?",
        kind: "single_select",
        required: true,
        options: [
          {
            optionCuid: "rating-q2-none",
            label: "No set date",
            value: "No set date",
          },
          {
            optionCuid: "rating-q2-1-3",
            label: "1–3 years",
            value: "1–3 years",
          },
          {
            optionCuid: "rating-q2-3-7",
            label: "3–7 years",
            value: "3–7 years",
          },
          {
            optionCuid: "rating-q2-7plus",
            label: "7+ years",
            value: "7+ years",
          },
        ],
      },
      {
        fieldCuid: "rating-q3",
        label: "If this dropped about 30% in a few months, what would you do?",
        kind: "single_select",
        required: true,
        options: [
          {
            optionCuid: "rating-q3-buy",
            label: "Buy more",
            value: "Buy more",
          },
          {
            optionCuid: "rating-q3-hold",
            label: "Hold",
            value: "Hold",
          },
          {
            optionCuid: "rating-q3-sell-some",
            label: "Sell some",
            value: "Sell some",
          },
          {
            optionCuid: "rating-q3-sell-all",
            label: "Sell all",
            value: "Sell all",
          },
        ],
      },
    ],
  };
